import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Phone, MapPin, StickyNote, Printer, Plus, Trash2, Wallet, TrendingUp, Pencil, ShoppingBag, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { HiddenMoney } from "../context/PriceVisibilityContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

function fmtMoney(n) {
  return Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40 inline" />;
  return dir === "asc" ? <ArrowUp className="h-3 w-3 text-teal-700 inline" /> : <ArrowDown className="h-3 w-3 text-teal-700 inline" />;
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPay, setOpenPay] = useState(false);
  const [pay, setPay] = useState({ amount: "", payment_date: new Date().toISOString().slice(0, 10), note: "" });
  const [saving, setSaving] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: "", phone: "", address: "", notes: "", total_amount: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [openPurchase, setOpenPurchase] = useState(false);
  const [purchase, setPurchase] = useState({ description: "", amount: "", purchase_date: new Date().toISOString().slice(0, 10), note: "" });
  const [savingPurchase, setSavingPurchase] = useState(false);
  const [deletingPurchase, setDeletingPurchase] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editPurchaseForm, setEditPurchaseForm] = useState({ description: "", amount: "", purchase_date: "", note: "" });
  const [savingEditPurchase, setSavingEditPurchase] = useState(false);
  const [payingPurchase, setPayingPurchase] = useState(null);
  const [purchasePay, setPurchasePay] = useState({ amount: "", payment_date: new Date().toISOString().slice(0, 10), note: "" });
  const [savingPurchasePay, setSavingPurchasePay] = useState(false);
  const [deletingPurchasePayment, setDeletingPurchasePayment] = useState(null);
  const [expandedPurchase, setExpandedPurchase] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({ amount: "", payment_date: "", note: "" });
  const [savingEditPayment, setSavingEditPayment] = useState(false);
  // Sorting states
  const [paySortBy, setPaySortBy] = useState(null); // 'payment_date' | 'amount' | 'remaining_after'
  const [paySortDir, setPaySortDir] = useState("asc");
  const [purSortBy, setPurSortBy] = useState(null); // 'description' | 'purchase_date' | 'amount' | 'total_paid' | 'remaining'
  const [purSortDir, setPurSortDir] = useState("asc");
  const togglePaySort = (key) => {
    if (paySortBy === key) {
      if (paySortDir === "asc") setPaySortDir("desc");
      else { setPaySortBy(null); setPaySortDir("asc"); }
    } else { setPaySortBy(key); setPaySortDir("asc"); }
  };
  const togglePurSort = (key) => {
    if (purSortBy === key) {
      if (purSortDir === "asc") setPurSortDir("desc");
      else { setPurSortBy(null); setPurSortDir("asc"); }
    } else { setPurSortBy(key); setPurSortDir("asc"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const submitPay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(pay.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("أدخل مبلغاً صحيحاً");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/customers/${id}/payments`, {
        amount: amt,
        payment_date: pay.payment_date,
        note: pay.note,
      });
      setCustomer(data);
      setOpenPay(false);
      setPay({ amount: "", payment_date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("تم تسجيل القسط");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async () => {
    if (!deletingPayment) return;
    try {
      const { data } = await api.delete(`/payments/${deletingPayment.id}`);
      if (data?.id) setCustomer(data);
      else load();
      setDeletingPayment(null);
      toast.success("تم حذف القسط");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const openEditDialog = () => {
    setEditForm({
      full_name: customer.full_name || "",
      phone: customer.phone || "",
      address: customer.address || "",
      notes: customer.notes || "",
      total_amount: String(customer.base_total_amount ?? customer.total_amount ?? ""),
    });
    setOpenEdit(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.full_name.trim() || !editForm.phone.trim()) {
      toast.error("الاسم ورقم الهاتف مطلوبان");
      return;
    }
    const amt = parseFloat(editForm.total_amount);
    if (isNaN(amt) || amt < 0) {
      toast.error("الرجاء إدخال مبلغ صحيح");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/customers/${id}`, {
        full_name: editForm.full_name.trim(),
        phone: editForm.phone.trim(),
        address: editForm.address.trim(),
        notes: editForm.notes.trim(),
        total_amount: amt,
      });
      setCustomer(data);
      setOpenEdit(false);
      toast.success("تم حفظ التعديلات");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const submitPurchase = async (e) => {
    e.preventDefault();
    if (!purchase.description.trim()) {
      toast.error("الرجاء كتابة وصف المشتريات");
      return;
    }
    const amt = parseFloat(purchase.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("أدخل مبلغاً صحيحاً");
      return;
    }
    setSavingPurchase(true);
    try {
      const { data } = await api.post(`/customers/${id}/purchases`, {
        description: purchase.description.trim(),
        amount: amt,
        purchase_date: purchase.purchase_date,
        note: purchase.note.trim(),
      });
      setCustomer(data);
      setOpenPurchase(false);
      setPurchase({ description: "", amount: "", purchase_date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("تم إضافة المشتريات وتحديث المبلغ الكلي");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingPurchase(false);
    }
  };

  const removePurchase = async () => {
    if (!deletingPurchase) return;
    try {
      const { data } = await api.delete(`/purchases/${deletingPurchase.id}`);
      if (data?.id) setCustomer(data);
      else load();
      setDeletingPurchase(null);
      toast.success("تم حذف العملية وتحديث المبلغ الكلي");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const openEditPurchase = (p) => {
    setEditingPurchase(p);
    setEditPurchaseForm({
      description: p.description || "",
      amount: String(p.amount || ""),
      purchase_date: p.purchase_date || new Date().toISOString().slice(0, 10),
      note: p.note || "",
    });
  };

  const submitEditPurchase = async (e) => {
    e.preventDefault();
    if (!editPurchaseForm.description.trim()) {
      toast.error("الوصف مطلوب");
      return;
    }
    const amt = parseFloat(editPurchaseForm.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("أدخل مبلغاً صحيحاً");
      return;
    }
    setSavingEditPurchase(true);
    try {
      const { data } = await api.put(`/purchases/${editingPurchase.id}`, {
        description: editPurchaseForm.description.trim(),
        amount: amt,
        purchase_date: editPurchaseForm.purchase_date,
        note: editPurchaseForm.note.trim(),
      });
      setCustomer(data);
      setEditingPurchase(null);
      toast.success("تم حفظ التعديلات");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingEditPurchase(false);
    }
  };

  const openPayPurchase = (p) => {
    setPayingPurchase(p);
    setPurchasePay({ amount: "", payment_date: new Date().toISOString().slice(0, 10), note: "" });
  };

  const submitPurchasePay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(purchasePay.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("أدخل مبلغاً صحيحاً");
      return;
    }
    setSavingPurchasePay(true);
    try {
      const { data } = await api.post(`/purchases/${payingPurchase.id}/payments`, {
        amount: amt,
        payment_date: purchasePay.payment_date,
        note: purchasePay.note.trim(),
      });
      setCustomer(data);
      setPayingPurchase(null);
      setExpandedPurchase(payingPurchase.id);
      toast.success("تم تسجيل القسط على المشتريات");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingPurchasePay(false);
    }
  };

  const removePurchasePayment = async () => {
    if (!deletingPurchasePayment) return;
    try {
      const { data } = await api.delete(`/purchase-payments/${deletingPurchasePayment.id}`);
      if (data?.id) setCustomer(data);
      else load();
      setDeletingPurchasePayment(null);
      toast.success("تم حذف القسط");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const openEditPayment = (p, kind) => {
    setEditingPayment({ ...p, kind }); // kind: 'main' | 'purchase'
    setEditPaymentForm({ amount: String(p.amount), payment_date: p.payment_date, note: p.note || "" });
  };

  const submitEditPayment = async (e) => {
    e.preventDefault();
    const amt = parseFloat(editPaymentForm.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("أدخل مبلغاً صحيحاً");
      return;
    }
    setSavingEditPayment(true);
    try {
      const url = editingPayment.kind === "main"
        ? `/payments/${editingPayment.id}`
        : `/purchase-payments/${editingPayment.id}`;
      const { data } = await api.put(url, {
        amount: amt,
        payment_date: editPaymentForm.payment_date,
        note: editPaymentForm.note.trim(),
      });
      setCustomer(data);
      setEditingPayment(null);
      toast.success("تم تعديل القسط");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingEditPayment(false);
    }
  };

  if (loading || !customer) {
    return <div className="max-w-5xl mx-auto py-20 text-center text-zinc-400">جاري التحميل...</div>;
  }

  const cumulative = [];
  let running = Number(customer.total_amount || 0);
  for (const p of (customer.payments || [])) {
    running = running - Number(p.amount || 0);
    cumulative.push({ ...p, remaining_after: running });
  }

  const sortedPayments = (() => {
    if (!paySortBy) return cumulative;
    const dir = paySortDir === "asc" ? 1 : -1;
    return [...cumulative].sort((a, b) => {
      if (paySortBy === "payment_date") return String(a.payment_date || "").localeCompare(String(b.payment_date || "")) * dir;
      if (paySortBy === "amount") return ((Number(a.amount) || 0) - (Number(b.amount) || 0)) * dir;
      if (paySortBy === "remaining_after") return ((Number(a.remaining_after) || 0) - (Number(b.remaining_after) || 0)) * dir;
      if (paySortBy === "note") return String(a.note || "").localeCompare(String(b.note || ""), "ar") * dir;
      return 0;
    });
  })();

  const sortedPurchases = (() => {
    const list = customer.purchases || [];
    if (!purSortBy) return list;
    const dir = purSortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      if (purSortBy === "description") return String(a.description || "").localeCompare(String(b.description || ""), "ar") * dir;
      if (purSortBy === "purchase_date") return String(a.purchase_date || "").localeCompare(String(b.purchase_date || "")) * dir;
      if (purSortBy === "amount") return ((Number(a.amount) || 0) - (Number(b.amount) || 0)) * dir;
      if (purSortBy === "total_paid") return ((Number(a.total_paid) || 0) - (Number(b.total_paid) || 0)) * dir;
      if (purSortBy === "remaining") return ((Number(a.remaining) || 0) - (Number(b.remaining) || 0)) * dir;
      return 0;
    });
  })();

  const progress = customer.total_amount > 0
    ? Math.min(100, (customer.total_paid / customer.total_amount) * 100)
    : 0;

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6 no-print">
        <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-to-dashboard-btn" className="hover:bg-zinc-100">
          <ArrowRight className="h-4 w-4 ms-1" />
          عودة للقائمة
        </Button>
        <div className="flex items-center gap-2">
          <Button
            onClick={openEditDialog}
            data-testid="edit-customer-btn"
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold"
          >
            <Pencil className="h-4 w-4 ms-1" />
            تعديل بيانات الزبون
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-detail-btn" className="border-zinc-300">
            <Printer className="h-4 w-4 sm:ms-2" />
            <span className="hidden sm:inline">طباعة</span>
          </Button>
        </div>
      </div>

      {/* Customer Header Card */}
      <div className="relative bg-gradient-to-br from-teal-700 via-teal-800 to-zinc-900 rounded-3xl p-5 sm:p-10 mb-6 text-center text-white overflow-hidden elevated-card print-area">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-teal-400/10 rounded-full blur-3xl" />

        <div className="relative">
          <h1 className="text-4xl sm:text-5xl md:text-6xl break-words font-display font-extrabold mb-6 tracking-tight pt-2" data-testid="customer-name">
            {customer.full_name}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10">
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mb-2 flex items-center justify-center gap-1.5 font-latin font-semibold">
                <Phone className="h-3 w-3" />
                رقم الهاتف
              </div>
              <div className="text-base font-bold num" style={{ direction: "ltr" }}>{customer.phone || "—"}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10">
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mb-2 flex items-center justify-center gap-1.5 font-latin font-semibold">
                <MapPin className="h-3 w-3" />
                العنوان
              </div>
              <div className="text-base font-bold">{customer.address || "—"}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10">
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mb-2 flex items-center justify-center gap-1.5 font-latin font-semibold">
                <StickyNote className="h-3 w-3" />
                ملاحظة
              </div>
              <div className="text-base font-bold">{customer.notes || "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Totals strip + progress */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-3">
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 subtle-card text-center">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1 font-latin font-semibold">المبلغ الكلي</div>
          <div className="text-2xl font-extrabold text-zinc-900 num" data-testid="detail-total"><HiddenMoney value={fmtMoney(customer.total_amount)} /></div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 subtle-card text-center">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1 font-latin font-semibold">المسدد</div>
          <div className="text-2xl font-extrabold text-emerald-600 num" data-testid="detail-paid"><HiddenMoney value={fmtMoney(customer.total_paid)} /></div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl p-5 subtle-card text-center text-white">
          <div className="text-xs text-white/90 uppercase tracking-wider mb-1 font-latin font-semibold">المتبقي</div>
          <div className="text-2xl font-extrabold num" data-testid="detail-remaining"><HiddenMoney value={fmtMoney(customer.remaining)} /></div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-4 subtle-card mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-zinc-500 uppercase tracking-wider font-latin font-semibold">نسبة التسديد</div>
          <div className="text-sm font-bold text-teal-700 num">{progress.toFixed(0)}%</div>
        </div>
        <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-600 to-emerald-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Purchases section */}
      <div className="bg-white border border-zinc-200 rounded-2xl elevated-card overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between bg-gradient-to-l from-amber-50 to-white">
          <h2 className="text-xl font-display font-bold text-zinc-900 flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-amber-600" />
            مشتريات إضافية
            {customer.purchases?.length > 0 && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold num">
                {customer.purchases.length}
              </span>
            )}
          </h2>
          <Button
            onClick={() => setOpenPurchase(true)}
            data-testid="add-purchase-btn"
            className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm shadow-amber-500/30 no-print font-bold"
          >
            <Plus className="h-4 w-4 ms-1" />
            إضافة مشتريات
          </Button>
        </div>
        {(!customer.purchases || customer.purchases.length === 0) ? (
          <div className="px-6 py-10 text-center text-zinc-400 text-sm">
            لم يتم إضافة مشتريات إضافية بعد — اضغط "إضافة مشتريات" لتسجيل ما يأخذه الزبون لاحقاً
          </div>
        ) : (
          <>
            {/* Sort bar */}
            <div className="px-6 py-2.5 bg-amber-50/40 border-b border-amber-100 flex items-center gap-2 flex-wrap text-xs no-print">
              <span className="text-zinc-500 font-bold">فرز:</span>
              {[
                { key: "description", label: "الوصف" },
                { key: "purchase_date", label: "التاريخ" },
                { key: "amount", label: "المبلغ" },
                { key: "total_paid", label: "المسدد" },
                { key: "remaining", label: "المتبقي" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => togglePurSort(opt.key)}
                  data-testid={`sort-pur-${opt.key}-btn`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-bold transition-colors ${
                    purSortBy === opt.key ? "bg-amber-500 text-white" : "bg-white text-zinc-600 hover:bg-amber-100 border border-amber-200"
                  }`}
                >
                  {opt.label}
                  {purSortBy === opt.key && (purSortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                </button>
              ))}
              {purSortBy && (
                <button onClick={() => { setPurSortBy(null); setPurSortDir("asc"); }} className="text-zinc-500 hover:text-red-600 font-bold ms-1">
                  ✕ إلغاء الفرز
                </button>
              )}
            </div>
            <div className="divide-y divide-zinc-100">
            {sortedPurchases.map((p) => {
              const isExpanded = expandedPurchase === p.id;
              return (
                <div key={p.id} data-testid={`purchase-row-${p.serial_number}`}>
                  <div className="px-6 py-4 hover:bg-amber-50/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center h-9 w-9 bg-amber-100 text-amber-700 rounded-lg font-bold text-sm num shrink-0">
                        {p.serial_number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-base font-bold text-zinc-900">{p.description}</span>
                          <span className="text-xs text-zinc-500 num">· {p.purchase_date}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm flex-wrap">
                          <span className="text-amber-700 font-bold num">المبلغ: <HiddenMoney value={fmtMoney(p.amount)} /> د.ع</span>
                          <span className="text-emerald-600 font-bold num">مسدد: <HiddenMoney value={fmtMoney(p.total_paid || 0)} /></span>
                          <span className={`font-bold num ${(p.remaining || 0) > 0 ? "text-orange-600" : "text-emerald-600"}`}>
                            متبقي: <HiddenMoney value={fmtMoney(p.remaining || 0)} />
                          </span>
                        </div>
                        {p.note && <div className="text-xs text-zinc-500 mt-1">📝 {p.note}</div>}
                      </div>
                      <div className="flex items-center gap-1 no-print shrink-0">
                        <button
                          onClick={() => openPayPurchase(p)}
                          data-testid={`pay-purchase-btn-${p.serial_number}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-xs font-bold shadow-sm transition-all"
                          title="إضافة قسط لهذه المشتريات"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          قسط
                        </button>
                        <button
                          onClick={() => setExpandedPurchase(isExpanded ? null : p.id)}
                          className="text-zinc-500 hover:text-amber-700 hover:bg-amber-50 px-2 py-1.5 rounded-lg text-xs font-bold transition-colors num"
                          data-testid={`toggle-purchase-${p.serial_number}`}
                        >
                          {p.payments?.length || 0} {isExpanded ? "▴" : "▾"}
                        </button>
                        <button
                          onClick={() => openEditPurchase(p)}
                          data-testid={`edit-purchase-btn-${p.serial_number}`}
                          className="text-zinc-400 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 rounded-lg inline-flex items-center justify-center transition-all"
                          title="تعديل"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeletingPurchase({ id: p.id, serial: p.serial_number, description: p.description })}
                          data-testid={`delete-purchase-btn-${p.serial_number}`}
                          className="text-zinc-400 hover:text-white hover:bg-red-600 h-8 w-8 rounded-lg inline-flex items-center justify-center transition-all"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="bg-amber-50/40 border-t border-amber-100 px-6 py-3">
                      {p.payments?.length === 0 ? (
                        <div className="text-xs text-zinc-500 py-2">لا توجد أقساط مسجلة لهذه المشتريات بعد</div>
                      ) : (
                        <table className="w-full text-right text-sm">
                          <thead>
                            <tr className="text-xs text-zinc-500 uppercase font-latin">
                              <th className="py-2 font-bold w-12">#</th>
                              <th className="py-2 font-bold">التاريخ</th>
                              <th className="py-2 font-bold">المبلغ المسدد</th>
                              <th className="py-2 font-bold">ملاحظة</th>
                              <th className="py-2 no-print w-12"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.payments.map((pp) => (
                              <tr key={pp.id} className="border-t border-amber-100" data-testid={`purchase-payment-${p.serial_number}-${pp.serial_number}`}>
                                <td className="py-2 num font-bold">
                                  <span className="inline-flex items-center justify-center h-6 w-6 bg-teal-100 text-teal-700 rounded text-xs">{pp.serial_number}</span>
                                </td>
                                <td className="py-2 text-zinc-700 num">{pp.payment_date}</td>
                                <td className="py-2 text-emerald-600 num font-bold">- <HiddenMoney value={fmtMoney(pp.amount)} /></td>
                                <td className="py-2 text-zinc-500 text-xs">{pp.note || "—"}</td>
                                <td className="py-2 no-print">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => openEditPayment(pp, "purchase")}
                                      className="text-zinc-400 hover:text-teal-700 p-1"
                                      title="تعديل"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setDeletingPurchasePayment({ id: pp.id, serial: pp.serial_number })}
                                      className="text-zinc-400 hover:text-red-600 p-1"
                                      title="حذف"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Payments */}
      <div className="bg-white border border-zinc-200 rounded-2xl elevated-card overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between bg-gradient-to-l from-teal-50 to-white">
          <h2 className="text-xl font-display font-bold text-zinc-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-teal-700" />
            سجل التسديد
          </h2>
          <Button
            onClick={() => setOpenPay(true)}
            data-testid="add-payment-btn"
            className="bg-teal-700 hover:bg-teal-800 text-white shadow-sm shadow-teal-700/30 no-print font-bold"
          >
            <Plus className="h-4 w-4 ms-1" />
            إضافة قسط
          </Button>
        </div>
        <table className="w-full text-right">
          <thead>
            <tr className="bg-zinc-50 border-b-2 border-zinc-200">
              <th className="px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider w-20 font-latin">#</th>
              <th onClick={() => togglePaySort("payment_date")} data-testid="sort-pay-date-th" className="px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none">
                <span className="inline-flex items-center gap-1.5">تاريخ التسديد <SortIcon active={paySortBy === "payment_date"} dir={paySortDir} /></span>
              </th>
              <th onClick={() => togglePaySort("amount")} data-testid="sort-pay-amount-th" className="px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none">
                <span className="inline-flex items-center gap-1.5">المبلغ المسدد <SortIcon active={paySortBy === "amount"} dir={paySortDir} /></span>
              </th>
              <th onClick={() => togglePaySort("remaining_after")} data-testid="sort-pay-remaining-th" className="px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none">
                <span className="inline-flex items-center gap-1.5">المتبقي بعد التسديد <SortIcon active={paySortBy === "remaining_after"} dir={paySortDir} /></span>
              </th>
              <th onClick={() => togglePaySort("note")} data-testid="sort-pay-note-th" className="px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider cursor-pointer hover:text-teal-700 select-none">
                <span className="inline-flex items-center gap-1.5">ملاحظة <SortIcon active={paySortBy === "note"} dir={paySortDir} /></span>
              </th>
              <th className="px-4 py-3 no-print w-24 text-center text-xs font-bold text-zinc-600 uppercase tracking-wider">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {sortedPayments.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-400">لا توجد أقساط بعد. اضغط "إضافة قسط" لتسجيل أول تسديد</td></tr>
            ) : sortedPayments.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 hover:bg-teal-50/40 transition-colors" data-testid={`payment-row-${p.serial_number}`}>
                <td className="px-4 py-4 text-sm font-bold num">
                  <span className="inline-flex items-center justify-center h-7 w-7 bg-teal-100 text-teal-700 rounded-md">
                    {p.serial_number}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-zinc-700 num">{p.payment_date}</td>
                <td className="px-4 py-4 text-sm font-bold text-emerald-600 num">- <HiddenMoney value={fmtMoney(p.amount)} /></td>
                <td className="px-4 py-4 text-sm font-bold text-zinc-900 num"><HiddenMoney value={fmtMoney(p.remaining_after)} /></td>
                <td className="px-4 py-4 text-sm text-zinc-500">{p.note || "—"}</td>
                <td className="px-4 py-4 no-print">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => openEditPayment(p, "main")}
                      data-testid={`edit-payment-btn-${p.serial_number}`}
                      className="text-zinc-400 hover:text-teal-700 hover:bg-teal-50 h-8 w-8 rounded-lg inline-flex items-center justify-center transition-all"
                      title="تعديل القسط"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeletingPayment({ id: p.id, serial: p.serial_number })}
                      data-testid={`delete-payment-btn-${p.serial_number}`}
                      className="text-zinc-400 hover:text-white hover:bg-red-600 h-8 w-8 rounded-lg inline-flex items-center justify-center transition-all"
                      title="حذف القسط"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Final remaining banner */}
      <div className="mt-6 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-2xl px-8 py-7 flex items-center justify-between shadow-xl shadow-orange-500/25">
        <div>
          <div className="text-xs uppercase tracking-wider text-white/80 mb-1 font-latin font-semibold">FINAL BALANCE</div>
          <div className="text-2xl font-display font-bold">المبلغ المتبقي الكلي</div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-extrabold num" data-testid="final-remaining"><HiddenMoney value={fmtMoney(customer.remaining)} /></div>
          <div className="text-xs text-white/80 mt-1 font-latin">د.ع · IQD</div>
        </div>
      </div>

      {/* Add Payment Dialog */}
      <Dialog open={openPay} onOpenChange={setOpenPay}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">إضافة قسط</DialogTitle>
            <DialogDescription>سجّل المبلغ المسدد وتاريخ التسديد. سيُضاف للجدول مرقماً تلقائياً.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPay} className="space-y-4">
            <div>
              <Label htmlFor="amt">المبلغ المسدد</Label>
              <Input
                id="amt"
                type="number"
                step="any"
                data-testid="payment-amount-input"
                value={pay.amount}
                onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                className="mt-1.5 num text-lg font-bold py-5"
              />
            </div>
            <div>
              <Label htmlFor="dt">تاريخ التسديد</Label>
              <Input
                id="dt"
                type="date"
                data-testid="payment-date-input"
                value={pay.payment_date}
                onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="pnote">ملاحظة (اختياري)</Label>
              <Input
                id="pnote"
                data-testid="payment-note-input"
                value={pay.note}
                onChange={(e) => setPay({ ...pay, note: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenPay(false)}>إلغاء</Button>
              <Button type="submit" disabled={saving} data-testid="payment-save-btn" className="bg-teal-700 hover:bg-teal-800 font-bold">
                {saving ? "جاري الحفظ..." : "تسجيل القسط"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete payment confirm */}
      <Dialog open={!!deletingPayment} onOpenChange={(o) => !o && setDeletingPayment(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-red-700 font-display">تأكيد حذف القسط</DialogTitle>
            <DialogDescription>سيتم حذف القسط رقم <span className="font-bold num">#{deletingPayment?.serial}</span> نهائياً ولا يمكن التراجع.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletingPayment(null)}>إلغاء</Button>
            <Button
              onClick={deletePayment}
              data-testid="confirm-delete-payment-btn"
              className="bg-red-600 hover:bg-red-700 font-bold"
            >
              نعم، احذف
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit customer */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">تعديل بيانات الزبون</DialogTitle>
            <DialogDescription>غيّر أي معلومة تخص هذا الزبون. سيتم حفظ التعديلات مباشرةً.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">الاسم الثلاثي</Label>
              <Input
                id="edit_name"
                data-testid="edit-customer-name-input"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="edit_phone">رقم الهاتف</Label>
              <Input
                id="edit_phone"
                data-testid="edit-customer-phone-input"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="mt-1.5 py-5 num"
                style={{ direction: "ltr", textAlign: "right" }}
              />
            </div>
            <div>
              <Label htmlFor="edit_address">العنوان</Label>
              <Input
                id="edit_address"
                data-testid="edit-customer-address-input"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="edit_notes">ملاحظة</Label>
              <Textarea
                id="edit_notes"
                data-testid="edit-customer-notes-input"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={2}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="edit_total">المبلغ الكلي (د.ع)</Label>
              <Input
                id="edit_total"
                type="number"
                step="any"
                data-testid="edit-customer-total-input"
                value={editForm.total_amount}
                onChange={(e) => setEditForm({ ...editForm, total_amount: e.target.value })}
                className="mt-1.5 py-5 num text-lg font-bold"
              />
              <p className="text-xs text-zinc-500 mt-1.5">⚠️ تغيير المبلغ الكلي سيؤثر على حساب المتبقي تلقائياً.</p>
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenEdit(false)}>
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={savingEdit}
                data-testid="edit-customer-save-btn"
                className="bg-teal-700 hover:bg-teal-800 text-white font-bold"
              >
                {savingEdit ? "جاري الحفظ..." : "حفظ التعديلات"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Purchase dialog */}
      <Dialog open={openPurchase} onOpenChange={setOpenPurchase}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display flex items-center gap-2">
              <ShoppingBag className="h-6 w-6 text-amber-600" />
              إضافة مشتريات جديدة
            </DialogTitle>
            <DialogDescription>
              عند إضافة شيء جديد يأخذه الزبون، سيُضاف المبلغ تلقائياً إلى مبلغه الكلي.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPurchase} className="space-y-4">
            <div>
              <Label htmlFor="pur_desc">المشتريات / الوصف</Label>
              <Input
                id="pur_desc"
                data-testid="purchase-description-input"
                value={purchase.description}
                onChange={(e) => setPurchase({ ...purchase, description: e.target.value })}
                placeholder="مثال: سماعة بلوتوث Samsung"
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="pur_amt">المبلغ (د.ع)</Label>
              <Input
                id="pur_amt"
                type="number"
                step="any"
                data-testid="purchase-amount-input"
                value={purchase.amount}
                onChange={(e) => setPurchase({ ...purchase, amount: e.target.value })}
                className="mt-1.5 py-5 num text-lg font-bold"
              />
            </div>
            <div>
              <Label htmlFor="pur_date">التاريخ</Label>
              <Input
                id="pur_date"
                type="date"
                data-testid="purchase-date-input"
                value={purchase.purchase_date}
                onChange={(e) => setPurchase({ ...purchase, purchase_date: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="pur_note">ملاحظة خاصة (اختياري)</Label>
              <Textarea
                id="pur_note"
                data-testid="purchase-note-input"
                value={purchase.note}
                onChange={(e) => setPurchase({ ...purchase, note: e.target.value })}
                rows={2}
                placeholder="أي تفاصيل إضافية..."
                className="mt-1.5"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenPurchase(false)}>
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={savingPurchase}
                data-testid="purchase-save-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold"
              >
                {savingPurchase ? "جاري الحفظ..." : "حفظ المشتريات"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete purchase confirm */}
      <Dialog open={!!deletingPurchase} onOpenChange={(o) => !o && setDeletingPurchase(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-red-700 font-display">تأكيد حذف العملية</DialogTitle>
            <DialogDescription>
              سيتم حذف <span className="font-bold text-zinc-900">"{deletingPurchase?.description}"</span> وخصم مبلغها من المبلغ الكلي للزبون.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletingPurchase(null)}>إلغاء</Button>
            <Button
              onClick={removePurchase}
              data-testid="confirm-delete-purchase-btn"
              className="bg-red-600 hover:bg-red-700 font-bold"
            >
              نعم، احذف
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Dialog */}
      <Dialog open={!!editingPurchase} onOpenChange={(o) => !o && setEditingPurchase(null)}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display flex items-center gap-2">
              <Pencil className="h-5 w-5 text-amber-600" />
              تعديل مشتريات
            </DialogTitle>
            <DialogDescription>عدّل تفاصيل العملية. تغيير المبلغ سيُحدّث المبلغ الكلي للزبون تلقائياً.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEditPurchase} className="space-y-4">
            <div>
              <Label htmlFor="edit_pur_desc">المشتريات / الوصف</Label>
              <Input
                id="edit_pur_desc"
                data-testid="edit-purchase-description-input"
                value={editPurchaseForm.description}
                onChange={(e) => setEditPurchaseForm({ ...editPurchaseForm, description: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="edit_pur_amt">المبلغ (د.ع)</Label>
              <Input
                id="edit_pur_amt"
                type="number"
                step="any"
                data-testid="edit-purchase-amount-input"
                value={editPurchaseForm.amount}
                onChange={(e) => setEditPurchaseForm({ ...editPurchaseForm, amount: e.target.value })}
                className="mt-1.5 py-5 num text-lg font-bold"
              />
            </div>
            <div>
              <Label htmlFor="edit_pur_date">التاريخ</Label>
              <Input
                id="edit_pur_date"
                type="date"
                data-testid="edit-purchase-date-input"
                value={editPurchaseForm.purchase_date}
                onChange={(e) => setEditPurchaseForm({ ...editPurchaseForm, purchase_date: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="edit_pur_note">ملاحظة (اختياري)</Label>
              <Textarea
                id="edit_pur_note"
                data-testid="edit-purchase-note-input"
                value={editPurchaseForm.note}
                onChange={(e) => setEditPurchaseForm({ ...editPurchaseForm, note: e.target.value })}
                rows={2}
                className="mt-1.5"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditingPurchase(null)}>إلغاء</Button>
              <Button type="submit" disabled={savingEditPurchase} data-testid="edit-purchase-save-btn" className="bg-amber-500 hover:bg-amber-600 text-white font-bold">
                {savingEditPurchase ? "جاري الحفظ..." : "حفظ التعديلات"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Payment to Purchase Dialog */}
      <Dialog open={!!payingPurchase} onOpenChange={(o) => !o && setPayingPurchase(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">إضافة قسط على المشتريات</DialogTitle>
            <DialogDescription>
              قسط مخصص لـ "<span className="font-bold text-zinc-900">{payingPurchase?.description}</span>"
              {payingPurchase && (
                <div className="mt-2 text-xs flex gap-3">
                  <span>المبلغ: <span className="num font-bold text-amber-700">{fmtMoney(payingPurchase.amount)}</span></span>
                  <span>المسدد: <span className="num font-bold text-emerald-600">{fmtMoney(payingPurchase.total_paid || 0)}</span></span>
                  <span>المتبقي: <span className="num font-bold text-orange-600">{fmtMoney(payingPurchase.remaining || 0)}</span></span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPurchasePay} className="space-y-4">
            <div>
              <Label htmlFor="pp_amt">المبلغ المسدد (د.ع)</Label>
              <Input id="pp_amt" type="number" step="any" data-testid="purchase-pay-amount-input"
                value={purchasePay.amount} onChange={(e) => setPurchasePay({ ...purchasePay, amount: e.target.value })}
                className="mt-1.5 py-5 num text-lg font-bold" />
            </div>
            <div>
              <Label htmlFor="pp_date">تاريخ التسديد</Label>
              <Input id="pp_date" type="date" data-testid="purchase-pay-date-input"
                value={purchasePay.payment_date} onChange={(e) => setPurchasePay({ ...purchasePay, payment_date: e.target.value })}
                className="mt-1.5 py-5" />
            </div>
            <div>
              <Label htmlFor="pp_note">ملاحظة (اختياري)</Label>
              <Input id="pp_note" data-testid="purchase-pay-note-input"
                value={purchasePay.note} onChange={(e) => setPurchasePay({ ...purchasePay, note: e.target.value })}
                className="mt-1.5 py-5" />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPayingPurchase(null)}>إلغاء</Button>
              <Button type="submit" disabled={savingPurchasePay} data-testid="purchase-pay-save-btn" className="bg-teal-700 hover:bg-teal-800 text-white font-bold">
                {savingPurchasePay ? "جاري الحفظ..." : "تسجيل القسط"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Purchase Payment Confirm */}
      <Dialog open={!!deletingPurchasePayment} onOpenChange={(o) => !o && setDeletingPurchasePayment(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-red-700 font-display">تأكيد حذف القسط</DialogTitle>
            <DialogDescription>سيتم حذف القسط رقم <span className="font-bold num">#{deletingPurchasePayment?.serial}</span> من سجل المشتريات.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletingPurchasePayment(null)}>إلغاء</Button>
            <Button onClick={removePurchasePayment} className="bg-red-600 hover:bg-red-700 font-bold">نعم، احذف</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog (works for both main and purchase payments) */}
      <Dialog open={!!editingPayment} onOpenChange={(o) => !o && setEditingPayment(null)}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display flex items-center gap-2">
              <Pencil className="h-5 w-5 text-teal-700" />
              تعديل {editingPayment?.kind === "purchase" ? "قسط مشتريات" : "قسط"}
            </DialogTitle>
            <DialogDescription>عدّل المبلغ أو التاريخ — مفيد إذا كتب المحاسب رقماً خاطئاً.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEditPayment} className="space-y-4">
            <div>
              <Label htmlFor="ep_amt">المبلغ المسدد (د.ع)</Label>
              <Input
                id="ep_amt"
                type="number"
                step="any"
                data-testid="edit-payment-amount-input"
                value={editPaymentForm.amount}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                className="mt-1.5 py-5 num text-lg font-bold"
              />
            </div>
            <div>
              <Label htmlFor="ep_date">تاريخ التسديد</Label>
              <Input
                id="ep_date"
                type="date"
                data-testid="edit-payment-date-input"
                value={editPaymentForm.payment_date}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_date: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <div>
              <Label htmlFor="ep_note">ملاحظة (اختياري)</Label>
              <Input
                id="ep_note"
                data-testid="edit-payment-note-input"
                value={editPaymentForm.note}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, note: e.target.value })}
                className="mt-1.5 py-5"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditingPayment(null)}>إلغاء</Button>
              <Button type="submit" disabled={savingEditPayment} data-testid="edit-payment-save-btn" className="bg-teal-700 hover:bg-teal-800 text-white font-bold">
                {savingEditPayment ? "جاري الحفظ..." : "حفظ التعديل"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>




    </div>
  );
}
